import { Typography } from '@mui/material';
import { ReactNode } from 'react';

import { SelectItemGroupLabel } from '@/components/form/baseInputs/SelectInput/SelectItemGroupLabel';
import { Trash01Icon } from '@/components/icons/Trash01Icon';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal/ConfirmationModal';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { useModalState } from '@/hooks/useModalState';

interface ConfirmRemoveAllMenuItemProps {
  /** Red destructive menu-item label (e.g. "Remove all primary beneficiaries"). */
  menuItemLabel: ReactNode;
  /** Confirmation dialog heading. */
  confirmHeading: string;
  /** Lead-in body copy; the shell appends a bold "This cannot be undone." */
  confirmBody: ReactNode;
  onConfirm: () => void;
}

/**
 * Presentational shell for a destructive "remove all" overflow-menu action: a
 * red trash menu item that opens a confirmation dialog. Callers own the actual
 * removal (`onConfirm`) and supply the copy, so this stays agnostic to any
 * particular form shape. Shared by the dispositive-provisions and
 * beneficiary-designation overflow menus.
 */
export function ConfirmRemoveAllMenuItem({
  menuItemLabel,
  confirmHeading,
  confirmBody,
  onConfirm,
}: ConfirmRemoveAllMenuItemProps) {
  const [{ isModalOpen }, { setModalIsOpen }] = useModalState();

  return (
    <>
      {isModalOpen && (
        <ConfirmationModal
          isOpen={isModalOpen}
          heading={confirmHeading}
          cancelText="Cancel"
          confirmText="Remove all"
          confirmButtonProps={{ variant: 'destructive-primary' }}
          onClose={(result) => {
            if (result === 'confirm') {
              onConfirm();
            }
            setModalIsOpen(false);
          }}
        >
          {confirmBody}{' '}
          <Typography component="span" variant="body-xs" fontWeight="bold">
            This cannot be undone.
          </Typography>
        </ConfirmationModal>
      )}
      <SelectItemGroupLabel />
      <MenuItem
        variant="destructive"
        icon={<Trash01Icon size={16} />}
        label={menuItemLabel}
        onClick={(event) => {
          event.stopPropagation();
          setModalIsOpen(true);
        }}
      />
    </>
  );
}
